import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import MainApp from "@/pages/MainApp";
import CalendarTab from '@/components/CalendarTab';
import CommunityTab from '@/components/CommunityTab';
import ProfileTab from '@/components/ProfileTab';
import AccountCenter from '@/components/AccountCenter';
import PublicProfilePage from '@/components/PublicProfilePage';
import AdminTab from '@/components/AdminTab';
import { Toaster } from "@/components/ui/sonner";
import { CacheProvider } from "@/context/CacheContext";

import TermsPage from "@/pages/TermsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/user/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          console.error('Failed to load user', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return { user, token, loading, login, logout };
};

function App() {
  const auth = useAuth();

  // Domain redirect
  if (window.location.hostname === 'challenge2026.emergent.host') {
    window.location.replace('https://12challenges.org' + window.location.pathname + window.location.search);
    return null;
  }

  if (auth.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  // Admin check helper
  const isUserAdmin = (user) => {
    if (!user) return false;
    const adminEmails = ['federicson7@gmail.com', 'p.panayiotou2@gmail.com'];
    return adminEmails.includes(user.email.toLowerCase());
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/reset" element={<ResetPasswordPage />} />
          <Route
            path="/auth"
            element={!auth.token ? <AuthPage auth={auth} /> : <Navigate to="/calendar" />}
          />
          <Route
            path="/app/*"
            element={<Navigate to="/calendar" replace />}
          />
          <Route
            path="/"
            element={auth.token ? (
              <CacheProvider auth={auth}>
                <MainApp auth={auth} />
              </CacheProvider>
            ) : <Navigate to="/auth" />}
          >
            <Route index element={<Navigate to="/calendar" replace />} />
            <Route path="calendar" element={<CalendarTab auth={auth} />} />
            <Route path="community" element={<CommunityTab auth={auth} />} />
            <Route path="profile" element={<ProfileTab auth={auth} />} />
            <Route path="profile/account" element={<AccountCenter auth={auth} />} />
            <Route path="user/:userId" element={<PublicProfilePage auth={auth} />} />
            <Route
              path="admin"
              element={isUserAdmin(auth.user) ? <AdminTab auth={auth} /> : <Navigate to="/calendar" replace />}
            />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;